import React from 'react';
import { FiLayers } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const SetsLesson = () => {
  const sections = [
    {
      id: 'set-basics',
      title: 'Set Basics & Notation',
      content: {
        introduction:
          'Sets are collections of distinct objects. Understanding set notation and basic operations is fundamental for solving GRE counting and probability problems.',

        concepts: [
          {
            title: 'Set Notation',
            explanation:
              'Sets are denoted with curly braces {}. Elements are listed inside: A = {1, 2, 3, 4, 5}. §∈§ means "is an element of", §∉§ means "is not an element of".',
            example: {
              problem: 'Let A = {2, 4, 6, 8}. Is §4 ∈ A§? Is §5 ∈ A§?',
              solution: [
                '§4 ∈ A§ means "4 is an element of A"',
                'Since 4 appears in the set, §4 ∈ A§ is TRUE',
                '§5 ∈ A§ would mean "5 is an element of A"',
                'Since 5 does not appear, §5 ∉ A§ (5 is NOT in A)',
              ],
            },
          },
          {
            title: 'Cardinality (Size)',
            explanation:
              'The cardinality of a set is the number of elements it contains, denoted §|A|§ or §n(A)§.',
            example: {
              problem: 'If B = {1, 3, 5, 7, 9, 11}, what is §|B|§?',
              solution: [
                'Count the distinct elements in set B',
                'B has 6 elements: 1, 3, 5, 7, 9, 11',
                '§|B| = 6§',
                'Answer: The cardinality is 6',
              ],
            },
          },
          {
            title: 'Empty Set',
            explanation:
              'The empty set §∅§ or {} contains no elements. §|∅| = 0§.',
            example: {
              problem:
                'Let §C§ be the set of integers that are both odd and even. What is §C§?',
              solution: [
                'No integer can be both odd and even',
                'Therefore, §C§ has no elements',
                '§C = ∅§ (the empty set)',
                '§|C| = 0§',
              ],
            },
          },
          {
            title: 'Subsets',
            explanation:
              '§A ⊆ B§ means every element of §A§ is also in §B§. Every set is a subset of itself. The empty set is a subset of every set.',
            example: {
              problem: 'Let A = {1, 2} and B = {1, 2, 3, 4}. Is §A ⊆ B§?',
              solution: [
                'Check if every element of §A§ is in §B§',
                '§1 ∈ B§? Yes ✓',
                '§2 ∈ B§? Yes ✓',
                'All elements of §A§ are in §B§',
                'Therefore §A ⊆ B§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'set-operations',
      title: 'Union & Intersection',
      content: {
        introduction:
          'Union combines sets, intersection finds common elements. These operations are crucial for solving overlapping group problems on the GRE.',

        concepts: [
          {
            title: 'Union (§A ∪ B§)',
            explanation:
              'The union §A ∪ B§ contains all elements in §A§ OR §B§ (or both). It combines the sets.',
            example: {
              problem: 'If A = {1, 2, 3} and B = {3, 4, 5}, what is §A ∪ B§?',
              solution: [
                'List all elements from either set',
                'From A: 1, 2, 3',
                'From B: 3, 4, 5',
                'Combined (no duplicates): {1, 2, 3, 4, 5}',
                '§A ∪ B§ = {1, 2, 3, 4, 5}',
              ],
            },
          },
          {
            title: 'Intersection (§A ∩ B§)',
            explanation:
              'The intersection §A ∩ B§ contains elements in BOTH §A§ AND §B§. It finds common elements.',
            example: {
              problem:
                'If A = {1, 2, 3, 4} and B = {3, 4, 5, 6}, what is §A ∩ B§?',
              solution: [
                'Find elements that appear in both sets',
                '§1§: in §A§ but not §B§ ✗',
                '§2§: in §A§ but not §B§ ✗',
                '§3§: in both §A§ and §B§ ✓',
                '§4§: in both §A§ and §B§ ✓',
                '§A ∩ B = \\{3, 4\\}§',
              ],
            },
          },
          {
            title: 'Disjoint Sets',
            explanation:
              'Sets are disjoint if they have no common elements: §A ∩ B = ∅§.',
            example: {
              problem: 'Are A = {1, 3, 5} and B = {2, 4, 6} disjoint?',
              solution: [
                'Check if any element appears in both',
                'Elements of §A§: 1, 3, 5 (all odd)',
                'Elements of §B§: 2, 4, 6 (all even)',
                'No common elements',
                '§A ∩ B = ∅§, so yes, they are disjoint',
              ],
            },
          },
          {
            title: 'Difference (§A - B§)',
            explanation:
              'The difference §A - B§ contains elements in §A§ but NOT in §B§.',
            example: {
              problem:
                'If A = {1, 2, 3, 4, 5} and B = {3, 4, 5, 6}, what is §A - B§?',
              solution: [
                'Find elements in §A§ but not in §B§',
                '§1§: in §A§, not in §B§ ✓',
                '§2§: in §A§, not in §B§ ✓',
                '§3, 4, 5§: in both, so excluded',
                '§A - B = \\{1, 2\\}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'venn-diagrams',
      title: 'Venn Diagrams',
      content: {
        introduction:
          'Venn diagrams visually represent set relationships. They are essential tools for solving overlapping group problems on the GRE.',

        concepts: [
          {
            title: 'Two-Set Venn Diagram',
            explanation:
              'A two-circle Venn diagram shows: elements only in §A§, only in §B§, in both, and in neither.',
            example: {
              problem:
                'In a class of 40 students, 25 study French, 20 study Spanish, and 10 study both. How many study neither?',
              solution: [
                'Draw Venn diagram with French and Spanish circles',
                'Both: §10§ students',
                'Only French: §25 - 10 = 15§ students',
                'Only Spanish: §20 - 10 = 10§ students',
                'Total studying at least one: §15 + 10 + 10 = 35§',
                'Neither: §40 - 35 = 5§ students',
              ],
            },
          },
          {
            title: 'Three-Set Venn Diagram',
            explanation:
              'Three circles create 7 distinct regions plus the outside. Label each region systematically.',
            example: {
              problem:
                'In a survey: 50 like coffee, 40 like tea, 30 like juice. 20 like coffee and tea, 15 like coffee and juice, 10 like tea and juice, 5 like all three. Total surveyed: 100. How many like none?',
              solution: [
                'Start with center (all three): §5§',
                'Coffee & tea only: §20 - 5 = 15§',
                'Coffee & juice only: §15 - 5 = 10§',
                'Tea & juice only: §10 - 5 = 5§',
                'Coffee only: §50 - 15 - 10 - 5 = 20§',
                'Tea only: §40 - 15 - 5 - 5 = 15§',
                'Juice only: §30 - 10 - 5 - 5 = 10§',
                'Total with at least one: §20+15+10+15+10+5+5 = 80§',
                'None: §100 - 80 = 20§',
              ],
            },
          },
          {
            title: 'Overlapping Groups Formula',
            explanation:
              'Key insight: when adding sets, elements counted multiple times must be subtracted.',
            example: {
              problem:
                'Of 100 employees: 60 have a car, 50 have a bike. If everyone has at least one, how many have both?',
              solution: [
                'Use formula: §|A ∪ B| = |A| + |B| - |A ∩ B|§',
                'We know: §|A ∪ B| = 100§ (everyone has at least one)',
                '§|A| = 60§ (car), §|B| = 50§ (bike)',
                '§100 = 60 + 50 - |A ∩ B|§',
                '§|A ∩ B| = 110 - 100 = 10§',
                '10 employees have both',
              ],
            },
          },
          {
            title: 'Complement in Venn Diagrams',
            explanation:
              'The complement §A^c§ (or §\\overline{A}§) includes everything NOT in §A§ within the universal set.',
            example: {
              problem:
                'In a universe of integers from 1 to 20, let §A§ = even numbers. What is §|A^c|§?',
              solution: [
                'Universal set: {1, 2, 3, ..., 20}, so §|U| = 20§',
                '§A§ = even numbers = {2, 4, 6, ..., 20}',
                '§|A| = 10§',
                '§A^c§ = odd numbers',
                '§|A^c| = 20 - 10 = 10§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'counting-subsets',
      title: 'Counting & Subsets',
      content: {
        introduction:
          'Understanding how to count subsets and apply the inclusion-exclusion principle is crucial for GRE combinatorics and probability problems.',

        concepts: [
          {
            title: 'Number of Subsets',
            explanation:
              'A set with §n§ elements has §2^n§ total subsets (including the empty set and the set itself).',
            example: {
              problem: 'How many subsets does A = {a, b, c} have?',
              solution: [
                'Use formula: §2^n§ subsets',
                '§|A| = 3§ elements',
                'Number of subsets = §2^3 = 8§',
                'List them: ∅, {a}, {b}, {c}, {a,b}, {a,c}, {b,c}, {a,b,c}',
                'Answer: 8 subsets',
              ],
            },
          },
          {
            title: 'Subsets of Size k',
            explanation:
              'The number of subsets of size §k§ from a set of size §n§ is §C(n,k) = n!/(k!(n-k)!)§.',
            example: {
              problem:
                'How many 2-element subsets can be formed from {1, 2, 3, 4}?',
              solution: [
                'Use combination formula: §C(n,k)§',
                '§n = 4, k = 2§',
                '§C(4,2) = 4!/(2!2!) = 24/(2×2) = 6§',
                'List them: {1,2}, {1,3}, {1,4}, {2,3}, {2,4}, {3,4}',
                'Answer: 6 subsets',
              ],
            },
          },
          {
            title: 'Inclusion-Exclusion Principle',
            explanation:
              'For two sets: §|A ∪ B| = |A| + |B| - |A ∩ B|§. For three sets: §|A ∪ B ∪ C| = |A| + |B| + |C| - |A ∩ B| - |A ∩ C| - |B ∩ C| + |A ∩ B ∩ C|§.',
            example: {
              problem:
                'Set §A§ has 40 elements, set §B§ has 150 elements, and 21 elements are in both. How many elements are in §A ∪ B§?',
              solution: [
                'Use inclusion-exclusion: §|A ∪ B| = |A| + |B| - |A ∩ B|§',
                '§|A| = 40§, §|B| = 150§, §|A ∩ B| = 21§',
                '§|A ∪ B| = 40 + 150 - 21§',
                '§= 169§',
                'Answer: §169§ elements in the union',
              ],
            },
          },
          {
            title: 'Subsets with Constraints',
            explanation:
              'When certain elements must or must not be included, adjust the counting accordingly.',
            example: {
              problem:
                'A set §S§ has 5 elements, one of which is special and MUST be included. How many 4-element subsets include the special element?',
              solution: [
                'The special element is already included',
                'Need to choose 3 more from the remaining 4 elements',
                '§\\binom{4}{3} = \\frac{4!}{3!1!} = 4§',
                'Answer: §4§ subsets',
                'Note: If we wanted all 4-element subsets without constraint, it would be §\\binom{5}{4} = 5§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If A = {2, 4, 6, 8} and B = {6, 8, 10, 12}, what is §A ∩ B§?',
      options: ['{6, 8}', '{2, 4}', '{10, 12}', '{2, 4, 6, 8, 10, 12}', '§∅§'],
      correct: 0,
      explanation:
        'Intersection contains elements in BOTH sets. §6§ and §8§ appear in both §A§ and §B§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'How many subsets does a set with 4 elements have?',
      options: ['§8§', '§12§', '§16§', '§24§', '§32§'],
      correct: 2,
      explanation:
        'Number of subsets = §2^n = 2^4 = 16§. This includes the empty set and the set itself.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a class of 30 students, 18 play soccer and 15 play basketball. If 8 play both sports, how many play neither?',
      options: ['§3§', '§5§', '§7§', '§10§', '§12§'],
      correct: 1,
      explanation:
        'Using inclusion-exclusion: §|S ∪ B| = 18 + 15 - 8 = 25§. Neither = §30 - 25 = 5§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'Set §A§ has 40 integers, set §B§ has 150 integers. If 24 integers are in both sets, how many integers are in §A ∪ B§?',
      options: ['§166§', '§190§', '§174§', '§214§', '§164§'],
      correct: 0,
      explanation: '§|A ∪ B| = |A| + |B| - |A ∩ B| = 40 + 150 - 24 = 166§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A set §S§ has 5 distinct elements, and one special element that MUST be in any counted subset. How many 4-element subsets satisfy this constraint?',
      options: ['§4§', '§5§', '§1§', '§20§', '§10§'],
      correct: 0,
      explanation:
        'Special element is fixed. Choose 3 from remaining 4: §\\binom{4}{3} = 4§.',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'Set A = §\\{2, 4, 6, 8\\}§, Set B = §\\{4, 8, 12\\}§. What is §A \\cup B§?',
      options: [
        '§\\{2, 4, 6, 8, 12\\}§',
        '§\\{4, 8\\}§',
        '§\\{2, 6, 12\\}§',
        '§\\{2, 4, 6, 8, 12, 16\\}§',
        '§\\{4, 8, 12\\}§',
      ],
      correct: 0,
      explanation:
        'Union §A \\cup B§ contains all elements from both sets without repetition: §\\{2, 4, 6, 8, 12\\}§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'In a class of 40 students, 28 study French, 20 study Spanish, and 5 study neither. How many study both?',
      options: ['§13§', '§12§', '§15§', '§10§', '§8§'],
      correct: 0,
      explanation:
        'Total studying at least one = §40 - 5 = 35§. §|F \\cup S| = |F| + |S| - |F \\cap S|§. So §35 = 28 + 20 - x§ → §x = 48 - 35 = 13§.',
    },
    {
      type: 'untimed',
      question: 'If A = {1, 2, 3} and B = {3, 4, 5}, what is §A ∪ B§?',
      options: ['{1, 2, 3, 4, 5}', '{3}', '{1, 2}', '{4, 5}', '{1, 2, 4, 5}'],
      correct: 0,
      explanation:
        'Union contains all elements from either set: {1, 2, 3, 4, 5}.',
    },
    {
      type: 'untimed',
      question: 'How many 2-element subsets can be formed from {a, b, c, d}?',
      options: ['§4§', '§6§', '§8§', '§12§', '§16§'],
      correct: 1,
      explanation:
        '§C(4,2) = 4!/(2!2!) = 6§. List: {a,b}, {a,c}, {a,d}, {b,c}, {b,d}, {c,d}.',
    },
    {
      type: 'untimed',
      question:
        'Of 100 people, 60 speak English, 50 speak French. Everyone speaks at least one language. How many speak both?',
      options: ['§10§', '§20§', '§30§', '§40§', '§50§'],
      correct: 0,
      explanation:
        '§|E ∪ F| = |E| + |F| - |E ∩ F|§. So §100 = 60 + 50 - |E ∩ F|§, giving §|E ∩ F| = 10§.',
    },
    {
      type: 'untimed',
      question: 'If A = {1, 3, 5, 7} and B = {2, 4, 6, 8}, what is §A ∩ B§?',
      options: [
        '§∅§',
        '§\\{1\\}§',
        '§\\{2\\}§',
        '§\\{1, 2, 3, 4, 5, 6, 7, 8\\}§',
        '§\\{5\\}§',
      ],
      correct: 0,
      explanation: 'No common elements (all odd vs all even), so §A ∩ B = ∅§.',
    },
    {
      type: 'untimed',
      question:
        'In a group of 50 people, 30 like pizza, 25 like burgers, and 10 like both. How many like neither?',
      options: ['§5§', '§10§', '§15§', '§20§', '§25§'],
      correct: 0,
      explanation:
        'At least one = §30 + 25 - 10 = 45§. Neither = §50 - 45 = 5§.',
    },
    {
      type: 'untimed',
      question:
        'If set X has 5 elements, how many subsets does X have in total?',
      options: ['§32§', '§16§', '§25§', '§10§', '§5§'],
      correct: 0,
      explanation:
        'Total subsets = §2^n§ where n is number of elements. §2^5 = 32§.',
    },
    {
      type: 'untimed',
      question:
        'Set M = §\\{x : x\\text{ is a prime number less than 10}\\}§. How many elements does M have?',
      options: ['§4§', '§3§', '§5§', '§6§', '§2§'],
      correct: 0,
      explanation:
        'Primes less than 10: §\\{2, 3, 5, 7\\}§. Total = 4 elements.',
    },
    {
      type: 'untimed',
      question:
        'Set A = §\\{1, 2, 3, 4\\}§, Set B = §\\{3, 4, 5, 6\\}§, Set C = §\\{4, 5, 6, 7\\}§. What is §A \\cap B \\cap C§?',
      options: [
        '§\\{4\\}§',
        '§\\{3, 4\\}§',
        '§\\{5\\}§',
        '§\\{3, 4, 5\\}§',
        '§\\emptyset§',
      ],
      correct: 0,
      explanation:
        '§A \\cap B = \\{3, 4\\}§. Then §\\{3, 4\\} \\cap C = \\{4\\}§. Answer: §\\{4\\}§.',
    },
    {
      type: 'timed',
      question: 'If A = {1, 2, 3} and B = {3, 4, 5}, what is §A ∪ B§?',
      options: ['{1, 2, 3, 4, 5}', '{3}', '{1, 2}', '{4, 5}', '{1, 2, 4, 5}'],
      correct: 0,
      explanation:
        'Union contains all elements from either set: {1, 2, 3, 4, 5}.',
    },
    {
      type: 'timed',
      question: 'How many 2-element subsets can be formed from {a, b, c, d}?',
      options: ['§4§', '§6§', '§8§', '§12§', '§16§'],
      correct: 1,
      explanation:
        '§C(4,2) = 4!/(2!2!) = 6§. List: {a,b}, {a,c}, {a,d}, {b,c}, {b,d}, {c,d}.',
    },
    {
      type: 'timed',
      question:
        'Of 100 people, 60 speak English, 50 speak French. Everyone speaks at least one language. How many speak both?',
      options: ['§10§', '§20§', '§30§', '§40§', '§50§'],
      correct: 0,
      explanation:
        '§|E ∪ F| = |E| + |F| - |E ∩ F|§. So §100 = 60 + 50 - |E ∩ F|§, giving §|E ∩ F| = 10§.',
    },
    {
      type: 'timed',
      question: 'If A = {1, 3, 5, 7} and B = {2, 4, 6, 8}, what is §A ∩ B§?',
      options: [
        '§∅§',
        '§\\{1\\}§',
        '§\\{2\\}§',
        '§\\{1, 2, 3, 4, 5, 6, 7, 8\\}§',
        '§\\{5\\}§',
      ],
      correct: 0,
      explanation: 'No common elements (all odd vs all even), so §A ∩ B = ∅§.',
    },
    {
      type: 'timed',
      question:
        'In a group of 50 people, 30 like pizza, 25 like burgers, and 10 like both. How many like neither?',
      options: ['§5§', '§10§', '§15§', '§20§', '§25§'],
      correct: 0,
      explanation:
        'At least one = §30 + 25 - 10 = 45§. Neither = §50 - 45 = 5§.',
    },
    {
      type: 'timed',
      question:
        'If set X has 5 elements, how many subsets does X have in total?',
      options: ['§32§', '§16§', '§25§', '§10§', '§5§'],
      correct: 0,
      explanation:
        'Total subsets = §2^n§ where n is number of elements. §2^5 = 32§.',
    },
    {
      type: 'timed',
      question:
        'Set M = §\\{x : x\\text{ is a prime number less than 10}\\}§. How many elements does M have?',
      options: ['§4§', '§3§', '§5§', '§6§', '§2§'],
      correct: 0,
      explanation:
        'Primes less than 10: §\\{2, 3, 5, 7\\}§. Total = 4 elements.',
    },
    {
      type: 'timed',
      question:
        'Set A = §\\{1, 2, 3, 4\\}§, Set B = §\\{3, 4, 5, 6\\}§, Set C = §\\{4, 5, 6, 7\\}§. What is §A \\cap B \\cap C§?',
      options: [
        '§\\{4\\}§',
        '§\\{3, 4\\}§',
        '§\\{5\\}§',
        '§\\{3, 4, 5\\}§',
        '§\\emptyset§',
      ],
      correct: 0,
      explanation:
        '§A \\cap B = \\{3, 4\\}§. Then §\\{3, 4\\} \\cap C = \\{4\\}§. Answer: §\\{4\\}§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'In a group of 40, 22 study French, 18 study Spanish, and 8 study both. Compare the two quantities.',
      quantityA: 'Students studying neither language',
      quantityB: '§8§',
      correct: 1,
      explanation:
        'Compare the two quantities directly. Quantity B is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'In a two-set Venn diagram, which regions are included in §A union B§? Select all that apply.',
      options: [
        'A only',
        'B only',
        'Both A and B',
        'Neither A nor B',
        'The complement of A',
      ],
      correct: [0, 1, 2],
      explanation:
        'The union includes anything in A or in B, including the overlap.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'In a group of 50, 28 play soccer, 24 play basketball, and 10 play both. How many play at least one sport?',
      correctAnswer: '42',
      acceptedAnswers: ['42'],
      explanation: 'Use §A union B = 28 + 24 - 10 = 42§.',
    },
  ];

  const quickReference = [
    'Union: A ∪ B (all elements)',
    'Intersection: A ∩ B (common elements)',
    '|A ∪ B| = |A| + |B| - |A ∩ B|',
    'Subset: A ⊆ B',
    'Total subsets = 2ⁿ',
  ];

  const getStartedWithProps = {
    title: 'Need help with Sets?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Sets and Set Theory for the GRE Quantitative section',
    aicontext:
      'Master set notation, operations, Venn diagrams, and the inclusion-exclusion principle',
  };

  return (
    <LessonTemplate
      lessonId="sets"
      lessonTitle="Sets"
      lessonIcon={<FiLayers />}
      lessonDescription="Master set notation, operations, Venn diagrams, and the inclusion-exclusion principle"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="sets" />}
    />
  );
};

export default SetsLesson;
